import { useCallback, useEffect, useState } from "react";

import {
  WORKSPACE_SIMULATION_POLICY_PREVIEW_HIDE,
  WORKSPACE_SIMULATION_POLICY_PREVIEW_OPEN,
  dispatchWorkspaceSimulationPolicyPreviewVisibility,
  type WorkspaceSimulationPolicyPreviewOpenDetail,
} from "./workspaceSimulationPolicyPreviewEvents";

export type WorkspaceSimulationPolicyPreviewState = {
  open: boolean;
  simulationId: string | null;
  title: string;
  policy: unknown;
  hide: () => void;
};

const EMPTY: Omit<WorkspaceSimulationPolicyPreviewState, "hide"> = {
  open: false,
  simulationId: null,
  title: "Policy",
  policy: null,
};

export function useWorkspaceSimulationPolicyPreview(): WorkspaceSimulationPolicyPreviewState {
  const [state, setState] = useState(EMPTY);

  const hide = useCallback(() => {
    setState(EMPTY);
  }, []);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<WorkspaceSimulationPolicyPreviewOpenDetail>).detail;
      if (!detail?.simulationId) return;
      setState({
        open: true,
        simulationId: detail.simulationId.trim(),
        title: detail.title.trim() || "Policy",
        policy: detail.policy ?? null,
      });
    };

    const onHide = () => {
      setState(EMPTY);
    };

    window.addEventListener(WORKSPACE_SIMULATION_POLICY_PREVIEW_OPEN, onOpen);
    window.addEventListener(WORKSPACE_SIMULATION_POLICY_PREVIEW_HIDE, onHide);
    return () => {
      window.removeEventListener(WORKSPACE_SIMULATION_POLICY_PREVIEW_OPEN, onOpen);
      window.removeEventListener(WORKSPACE_SIMULATION_POLICY_PREVIEW_HIDE, onHide);
    };
  }, []);

  useEffect(() => {
    dispatchWorkspaceSimulationPolicyPreviewVisibility({
      open: state.open,
      simulationId: state.simulationId,
    });
  }, [state.open, state.simulationId]);

  return { ...state, hide };
}
